import {FunctionComponent, useCallback, useMemo} from 'react';
import {FieldArray, Formik} from 'formik';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router';

import Button from '@/components/button';
import SectionTitle from '@/components/section-title';
import {BUTTON_THEME} from '@/constants/button';
import {useCreateAllocation, useUpdateAllocation} from '@/hooks/queries/allocations';
import {Toaster} from '@/initializers/toaster';
import {Allocation, AllocationAccountType} from '@/repositories/beneva-api/endpoints/allocations';
import {WEB_CLICK_TRACKING_KEY} from '@/services/analytics/web-click-tracking-key';
import {WatchDirtyChange} from '../form/watch-dirty-change';
import {AllocationAccountTypeField} from './allocation-account-type-field';
import {AllocationFormGuaranteedInvestmentFundsTable} from './allocation-form-guaranteed-investment-funds-table';
import {AllocationFormInvestmentAccountsTable} from './allocation-form-investment-accounts-table';
import {AllocationFormSchema, AllocationFormSchemaType} from './allocation-form-schema';
import {AllocationNameField} from './allocation-name-field';
import {AllocationTotalField} from './allocation-total-field';
import {computeAmountSum} from './utils';

type Props = {
  allocation?: Allocation;
  onDirtyChange: (isDirty: boolean) => void;
};

const AdvisorPredeterminedAllocationForm: FunctionComponent<Props> = ({allocation, onDirtyChange}) => {
  const {t} = useTranslation();
  const navigate = useNavigate();

  const {mutateAsync: createAllocation, isPending: isCreating} = useCreateAllocation();
  const {mutateAsync: updateAllocation, isPending: isUpdating} = useUpdateAllocation();

  const isSubmitting = isCreating || isUpdating;

  const initialValues = useMemo((): AllocationFormSchemaType => {
    const accounts = allocation?.accounts ?? [];
    const accountType = allocation?.accountType ?? AllocationAccountType.CI;
    const name = allocation?.name ?? '';
    const amountTotal = computeAmountSum(accounts);

    return {name, accountType, amountTotal, accounts};
  }, [allocation?.accounts, allocation?.name, allocation?.accountType]);

  const submitForm = useCallback(
    async (values: typeof initialValues) => {
      onDirtyChange(false);

      try {
        const {name, accounts, accountType} = values;

        const newAllocation = {name, accountType, accounts};
        if (allocation) {
          await updateAllocation({allocationId: allocation.id, updatedAllocation: newAllocation});
        } else {
          await createAllocation(newAllocation);
        }

        navigate('/app/profile?tab=allocations');
      } catch (_error) {
        onDirtyChange(true);
        Toaster.error(t('constants.feedback-message.default.error'));
      }
    },
    [onDirtyChange, allocation, navigate, updateAllocation, createAllocation, t],
  );

  const onCancel = useCallback(() => {
    navigate('/app/profile?tab=allocations');
  }, [navigate]);

  return (
    <Formik initialValues={initialValues} validationSchema={AllocationFormSchema} onSubmit={submitForm}>
      {({handleSubmit, values, isValid}) => {
        return (
          <>
            <SectionTitle>{t('components.advisor-predetermined-allocation-form.allocation-details')}</SectionTitle>

            <form onSubmit={handleSubmit}>
              <div className="flex items-start gap-4">
                <AllocationNameField />

                <div className="flex w-2/5">
                  <AllocationAccountTypeField />
                </div>

                <div className="flex w-1/5">
                  <AllocationTotalField />
                </div>
              </div>

              <FieldArray
                name="accounts"
                render={arrayHelpers => (
                  <>
                    {values.accountType === AllocationAccountType.FPG ? (
                      <AllocationFormGuaranteedInvestmentFundsTable arrayHelpers={arrayHelpers} />
                    ) : (
                      <AllocationFormInvestmentAccountsTable arrayHelpers={arrayHelpers} />
                    )}
                  </>
                )}
              />

              <div className="mt-6 flex justify-end gap-4">
                <Button
                  type="button"
                  theme={BUTTON_THEME.SECONDARY}
                  label={t('components.advisor-predetermined-allocation-form.cancel')}
                  onClick={onCancel}
                  trackingKey={WEB_CLICK_TRACKING_KEY.CANCEL_EDIT_ALLOCATION}
                  disabled={isSubmitting}
                />
                <Button
                  type="submit"
                  theme={BUTTON_THEME.PRIMARY}
                  label={t('components.advisor-predetermined-allocation-form.save')}
                  onClick={() => {}}
                  trackingKey={WEB_CLICK_TRACKING_KEY.SAVE_ALLOCATION}
                  disabled={isSubmitting || !isValid}
                  isLoading={isSubmitting}
                />
              </div>
            </form>

            <WatchDirtyChange onDirtyChange={onDirtyChange} />
          </>
        );
      }}
    </Formik>
  );
};

export default AdvisorPredeterminedAllocationForm;
